import React from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { ISchedule, IScheduleBlock } from '@/types/schedule';
import type { Hue } from '@/types/styles';

import { useAppSelector, useTimezone } from '@/hooks';
import { CrossIcon } from '@/assets/icons';
import { formatDate, formatStartEndTime } from '@/helpers';
import { colors } from '@/styles/colors';
import { Button } from '@/components/common';
import { selectSchedulesList } from '@/store/selectors/scheduleSelectors';
import { getDateFromBlock } from '@/helpers/block';

interface IProps {
  blockColor: Hue;
  block: IScheduleBlock;
  contract: ISchedule;
  label?: React.ReactNode;
  buttonSubmit: string;
  handleSubmit: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose?: () => void;
}

const baseShiftModalContentSelector = createSelector(selectSchedulesList, schedulesList => ({
  schedulesList,
}));

const BaseShiftModalContent = ({
  contract,
  handleClose,
  buttonSubmit,
  handleSubmit,
  block,
  blockColor,
  label,
}: IProps): React.ReactElement => {
  const { timezoneName } = useTimezone();
  const { schedulesList } = useAppSelector(baseShiftModalContentSelector);

  const scheduleIndex = schedulesList.findIndex(schedule => schedule.scheduleId === contract.scheduleId) + 1;

  const { startBlock, endBlock } = block;
  const { scheduleName, clientName, scheduleNameShort } = contract;

  return (
    <>
      <BaseShiftModalHeader blockColor={blockColor}>
        {scheduleIndex}. {scheduleNameShort}
        <BaseShiftModalLabel>{label}</BaseShiftModalLabel>
        <BaseShiftModalCloseButton onClick={handleClose}>
          <img src={CrossIcon} alt="CrossIcon" />
        </BaseShiftModalCloseButton>
      </BaseShiftModalHeader>
      <BaseShiftModalTitle blockColor={blockColor}>{clientName}</BaseShiftModalTitle>
      <BaseShiftModalText>{scheduleName}</BaseShiftModalText>
      <BaseShiftModalText>{formatDate(getDateFromBlock(startBlock, timezoneName), 'EEE d')}</BaseShiftModalText>
      <BaseShiftModalTimeSpan>
        {`
          ${formatStartEndTime(
            getDateFromBlock(startBlock, timezoneName),
            getDateFromBlock(endBlock + 1, timezoneName),
          )}
        `}
      </BaseShiftModalTimeSpan>
      <BaseShiftModalButtonSubmit color="blue" size="l" type="submit" onClick={handleSubmit}>
        {buttonSubmit}
      </BaseShiftModalButtonSubmit>
    </>
  );
};

export { BaseShiftModalContent };

const BaseShiftModalHeader = styled.div<{ blockColor: Hue }>`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 4px;

  color: ${props => colors[props.blockColor].c500};
  font-size: 10px;
  font-weight: 700;
`;

const BaseShiftModalCloseButton = styled.button`
  border: none;
  background: none;
  margin-left: auto;
`;

const BaseShiftModalTitle = styled.div<{ blockColor: Hue }>`
  display: flex;
  flex-direction: row;
  align-items: center;

  margin-bottom: 6px;
  width: 100%;

  font-weight: 700;
  font-size: 14px;
  color: ${colors.gray.c600};

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    background-color: ${props => colors[props.blockColor].c500};
    border-radius: 50%;
    margin-right: 0.5rem;
  }
`;

const BaseShiftModalText = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #5a5a5a;

  margin-bottom: 4px;
  height: 18px;
  width: 100%;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const BaseShiftModalTimeSpan = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #4b5563;
  height: 18px;
`;

const BaseShiftModalButtonSubmit = styled(Button).attrs({
  className: 'w-100',
})`
  margin-top: 12px;
  background-color: #0087dd !important;
  color: #fff;

  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  height: 40px;
`;

const BaseShiftModalLabel = styled.span`
  margin-left: 8px;
  color: #000;
`;
